import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import PhoneInput, { findCountry } from "@/components/PhoneInput";
import { toast } from "sonner";
import { Loader2, ChevronLeft } from "lucide-react";
import { openGoogleRedirect } from "@/lib/googleAuth";
import api, { errMsg } from "@/lib/api";
import { TermsLink } from "@/components/TermsModal";

/**
 * Simplified SignUp — collects only Name + Mobile initially. The rest
 * (category / age / city) is deferred to Onboarding so the friction of
 * account creation is minimal.
 *  - Step 1: Name + Mobile (+91 fixed prefix, 10-digit constraint) + Terms.
 *  - Step 2: Verify OTP → backend fires a "Welcome to NextScene" WhatsApp
 *    (mocked, ready for Twilio) and returns a JWT.
 *  - After OTP verify → Onboarding (age gate + primary talent).
 */
export default function SignUp() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1 = name+phone, 2 = otp
  const [name, setName] = useState("");
  const [dial, setDial] = useState("+91");
  const [phone, setPhone] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [terms, setTerms] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpHint, setOtpHint] = useState("");
  const [busy, setBusy] = useState(false);

  const goGoogle = () => openGoogleRedirect({ redirectTo: "/app" });

  const sendOtp = async (e) => {
    e?.preventDefault?.();
    const country = findCountry(dial);
    if (!name.trim()) return toast.error("Enter your full name");
    if (phone.length !== country.max) return toast.error(`Enter your ${country.max}-digit mobile number`);
    if (!terms) return toast.error("Please accept the Terms & Conditions");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/send-otp", { phone_number: `${dial}${phone}` });
      setStep(2);
      setOtpHint(data.hint || "");
      toast.success(data.hint ? "OTP sent (MOCK)" : "OTP sent to your phone");
    } catch (err) { toast.error(errMsg(err, "Could not send OTP")); }
    finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    if (otp.length !== 6) return toast.error("Enter the 6-digit OTP");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/verify-otp", {
        phone_number: `${dial}${phone}`,
        code: otp,
        name: name.trim(),
        referral_code: referralCode.trim().toUpperCase() || null,
        terms_accepted: true,
      });
      localStorage.setItem("ns_token", data.token);
      setUser(data.user);
      toast.success(data.is_new ? "Welcome to NextScene! Check WhatsApp for your welcome message." : "Welcome back!");
      // New sign-ups always go through onboarding; existing users skip
      nav(data.needs_onboarding ? "/app/onboarding" : "/app");
    } catch (err) { toast.error(errMsg(err, "Invalid OTP")); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0605] flex items-center justify-center px-6 py-10 grain">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Logo size="lg" className="justify-center inline-flex"/>
          <p className="mt-3 text-zinc-400 text-sm">Claim your name. Build your first performance.</p>
        </div>

        <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-6">
          {step === 1 ? (
            <div className="space-y-4">
              <p className="text-center text-xs uppercase tracking-[0.2em] text-zinc-400">Sign up with</p>
              <button
                data-testid="signup-google"
                onClick={goGoogle}
                className="w-full h-11 rounded-full bg-white text-black hover:bg-zinc-100 font-medium inline-flex items-center justify-center gap-2 transition-colors"
              >
                <GoogleIcon/> Google
              </button>

              <div className="my-4 flex items-center gap-3 text-xs text-zinc-500">
                <div className="flex-1 h-px bg-zinc-800"/> or with mobile <div className="flex-1 h-px bg-zinc-800"/>
              </div>

              <form onSubmit={sendOtp} className="space-y-4" data-testid="signup-form-step1">
                <div>
                  <Label className="text-zinc-300 text-xs">Full name</Label>
                  <Input
                    data-testid="signup-name"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="mt-1 bg-zinc-900 border-zinc-800 focus-visible:ring-[#DC2626]"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Mobile number</Label>
                  <PhoneInput
                    dial={dial}
                    onDialChange={(v) => { setDial(v); setPhone(""); }}
                    number={phone}
                    onNumberChange={setPhone}
                    testIdPrefix="signup-phone"
                  />
                  <p className="text-[11px] text-zinc-500 mt-1">We'll send a 6-digit OTP to verify. Also unlocks WhatsApp casting alerts.</p>
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Got a referral code? <span className="text-zinc-500 font-normal">(optional)</span></Label>
                  <Input
                    data-testid="signup-referral"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 8))}
                    placeholder="8-character invite code"
                    className="mt-1 bg-zinc-900 border-zinc-800 focus-visible:ring-[#DC2626] uppercase tracking-widest font-mono"
                  />
                  <p className="text-[11px] text-zinc-500 mt-1">Adds bonus coins to your referrer when you verify.</p>
                </div>
                <label className="flex items-start gap-3 text-sm">
                  <Checkbox data-testid="signup-terms" checked={terms} onCheckedChange={(v) => setTerms(!!v)} className="mt-0.5 border-zinc-600 data-[state=checked]:bg-[#DC2626] data-[state=checked]:text-white"/>
                  <span className="text-zinc-400 leading-snug">I accept the <TermsLink>Terms & Conditions</TermsLink> and Community Guidelines.</span>
                </label>
                <Button
                  data-testid="signup-submit"
                  disabled={busy || !name.trim() || phone.length !== findCountry(dial).max || !terms}
                  type="submit"
                  className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold"
                >
                  {busy && <Loader2 className="animate-spin mr-2" size={16}/>} Send OTP
                </Button>
              </form>
            </div>
          ) : (
            <div className="space-y-4" data-testid="signup-form-step2">
              <button type="button" data-testid="signup-back" onClick={() => { setStep(1); setOtp(""); }} className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1"><ChevronLeft size={12}/> Back</button>
              <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 px-3 py-2 flex items-center justify-between">
                <span className="text-sm text-zinc-300">OTP sent to {dial} {phone}</span>
              </div>
              <form onSubmit={verifyOtp} className="space-y-4">
                <div>
                  <Label className="text-zinc-300 text-xs">6-digit OTP</Label>
                  <Input
                    data-testid="signup-otp"
                    inputMode="numeric"
                    maxLength={6}
                    required
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    className="mt-1 bg-zinc-900 border-zinc-800 focus-visible:ring-[#DC2626] tracking-[0.35em] text-center text-lg font-mono"
                  />
                  {otpHint && <p className="text-[11px] text-amber-300 mt-1">MOCK: use {otpHint.replace(/^MOCK OTP:\s*/, "")}</p>}
                </div>
                <Button data-testid="signup-verify" disabled={busy || otp.length !== 6} type="submit" className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">
                  {busy ? <Loader2 className="animate-spin" size={16}/> : "Verify & create account"}
                </Button>
                <button type="button" data-testid="signup-resend" onClick={sendOtp} disabled={busy} className="w-full text-xs text-zinc-400 hover:text-white">Didn't get it? Resend</button>
              </form>
            </div>
          )}
        </div>

        <p className="text-center mt-4 text-sm text-zinc-400">
          Already on NextScene? <Link data-testid="go-login" to="/login" className="text-[#DC2626] hover:underline">Log in</Link>
        </p>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
